import type { ComponentType, ReactNode } from 'react';
import Countdown from 'react-countdown';

import classNames from 'classnames';

import { Time } from '@uikit/icons/Time';
import { User } from '@uikit/icons/User';

import type { RewardPool } from '@shared/api/types/rewardPools';
import { getDistributionPolicyFlags } from '@shared/entities/rewardPool';
import { renderTimeAfterDays } from '@shared/helpers/react-countdown';
import useRerenderAfterDate from '@shared/hooks/useRerenderAfterDate';

import {
  isRewardPoolActive,
  isRewardPoolUpcoming,
} from '@store:web/components/entities/rewardPool';
import { Ticket } from '@ui/uikit/components/icons/mono/Ticket';

const getTimeLeftLabel = (date: string) => {
  return {
    label: (
      <>
        Ends in{' '}
        <span suppressHydrationWarning className="inline-block min-w-16 text-left">
          <Countdown date={date} renderer={renderTimeAfterDays} />
        </span>
      </>
    ),
    iconClassName: 'text-base-text-green',
    Icon: Time,
  };
};

const getTicketsLeftLabel = (availableTickets: number) => {
  return {
    label: `${availableTickets} Tickets Left`,
    iconClassName: 'text-base-text-green',
    Icon: Ticket,
  };
};

const getRewardPoolParticipationTagConfig = (
  rewardPool: RewardPool,
): {
  label: ReactNode;
  Icon: ComponentType<{ className: string }>;
  iconClassName?: string;
  className?: string;
} => {
  const { isProRata, isRaffleV1, isRaffleV2, isRaffleSwap } = getDistributionPolicyFlags(
    rewardPool.distribution_policy,
  );

  if (isRewardPoolUpcoming(rewardPool)) {
    return {
      label: rewardPool.minor_settings.coming_soon ? (
        'Coming Soon'
      ) : (
        <>
          Starts in{' '}
          <span suppressHydrationWarning className="inline-block min-w-16 text-left">
            <Countdown date={rewardPool.start_date} renderer={renderTimeAfterDays} />
          </span>
        </>
      ),
      iconClassName: 'text-base-text-tertiary',
      Icon: Time,
    };
  } else if (isRewardPoolActive(rewardPool)) {
    if (isProRata || ((isRaffleV1 || isRaffleSwap) && !rewardPool.participants_max)) {
      return getTimeLeftLabel(rewardPool.end_date);
    }

    if (isRaffleV2) {
      const availableTickets = Math.max(
        (rewardPool.entries_max || 0) - (rewardPool.entries_counter || 0),
        0,
      );

      switch (rewardPool.completion_trigger) {
        case 'SOLD_OUT': {
          return getTicketsLeftLabel(availableTickets);
        }

        case 'TIME': {
          return getTimeLeftLabel(rewardPool.end_date);
        }

        case 'TIME_OR_SOLD_OUT': {
          const endDate = new Date(rewardPool.end_date).getTime();
          const days = Math.floor((endDate - Date.now()) / (1000 * 60 * 60 * 24));

          if (days > 0) {
            return getTicketsLeftLabel(availableTickets);
          } else {
            return getTimeLeftLabel(rewardPool.end_date);
          }
        }
      }
    }

    const availableSlots = (rewardPool.participants_max || 0) - rewardPool.participants_counter;

    return {
      label: `${availableSlots} / ${rewardPool.participants_max} Spots Left`,
      iconClassName: 'text-base-text-green',
      Icon: User,
    };
  }

  return {
    label: 'Ended',
    className: 'text-base-text-tertiary',
    Icon: Time,
  };
};

interface RewardPoolCardParticipationTagProps {
  rewardPool: RewardPool;
  className?: string;
}

const RewardPoolCardParticipationTag = ({
  rewardPool,
  className,
}: RewardPoolCardParticipationTagProps) => {
  const {
    Icon,
    label,
    className: configClassName,
    iconClassName,
  } = getRewardPoolParticipationTagConfig(rewardPool);

  // NOTE: rerender component because some parts of ui depends on those dates
  useRerenderAfterDate(rewardPool.start_date);
  useRerenderAfterDate(rewardPool.end_date);

  return (
    <p
      className={classNames(
        'rounded-full flex text-xs gap-1.5 items-center font-semibold xl:text-sm text-center text-[#09090B] justify-center min-h-10 px-3 py-2 bg-[#F3F3F7]',
        configClassName,
        className,
      )}
      data-qa="participation_tag"
    >
      <Icon className={classNames('icon-xs', iconClassName)} /> {label}
    </p>
  );
};

export default RewardPoolCardParticipationTag;
